import axios, { AxiosError } from "axios";
import type {
  Activity,
  AppNotification,
  Body,
  Color,
  Comment,
  Epic,
  EpicListItem,
  ListItem,
  PRLink,
  ProjectBundle,
  ProjectFile,
  ProjectListItem,
  Project,
  PropertyOption,
  PropertyType,
  EntityType,
  Role,
  SearchResult,
  Sprint,
  SprintListItem,
  Stage,
  StageKind,
  Task,
  TaskListItem,
  User,
} from "./types";

export const http = axios.create({
  baseURL: "/api",
  withCredentials: true,
  headers: { "X-Requested-With": "XMLHttpRequest" },
});

export function errorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error.charAt(0).toUpperCase() + data.error.slice(1);
    if (err.response?.status === 413) return "The file is too large";
    if (!err.response) return "Can't reach the server. Check your connection.";
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

export function isStatus(err: unknown, status: number) {
  return err instanceof AxiosError && err.response?.status === status;
}

const get = <T>(url: string, params?: Record<string, unknown>) => http.get<T>(url, { params }).then((r) => r.data);
const post = <T = void>(url: string, body?: unknown) => http.post<T>(url, body).then((r) => r.data);
const patch = <T = void>(url: string, body?: unknown) => http.patch<T>(url, body).then((r) => r.data);
const put = <T = void>(url: string, body?: unknown) => http.put<T>(url, body).then((r) => r.data);
const del = <T = void>(url: string, params?: Record<string, unknown>) => http.delete<T>(url, { params }).then((r) => r.data);

const p = (pid: string) => `/projects/${pid}`;

export const api = {
  // auth
  setupStatus: () => get<{ needsSetup: boolean }>("/auth/setup"),
  setup: (body: { name: string; email: string; password: string }) => post<User>("/auth/setup", body),
  login: (body: { email: string; password: string }) => post<User>("/auth/login", body),
  logout: () => post("/auth/logout"),
  me: () => get<User>("/auth/me"),
  updateMe: (body: { name: string }) => patch<User>("/auth/me", body),
  changePassword: (body: { currentPassword: string; newPassword: string }) => post("/auth/me/password", body),

  // users
  users: () => get<User[]>("/users"),
  createUser: (body: { name: string; email: string; password: string; isAdmin: boolean }) => post<User>("/users", body),
  updateUser: (id: string, body: Partial<{ name: string; email: string; isAdmin: boolean; isActive: boolean; password: string }>) =>
    patch<User>(`/users/${id}`, body),

  // notifications
  notifications: () => get<{ items: AppNotification[]; unread: number }>("/notifications"),
  readNotification: (id: string) => post(`/notifications/${id}/read`),
  readAllNotifications: () => post("/notifications/read-all"),

  // projects
  projects: () => get<ProjectListItem[]>("/projects"),
  createProject: (body: { name: string; key: string; description: string }) => post<Project>("/projects", body),
  project: (pid: string) => get<ProjectBundle>(`${p(pid)}/`),
  updateProject: (pid: string, body: Partial<{ name: string; key: string; description: string }>) => patch<Project>(`${p(pid)}/`, body),
  deleteProject: (pid: string) => del(`${p(pid)}/`),
  setMember: (pid: string, userId: string, role: Role) => put(`${p(pid)}/members/${userId}`, { role }),
  removeMember: (pid: string, userId: string) => del(`${p(pid)}/members/${userId}`),
  search: (pid: string, q: string) => get<SearchResult[]>(`${p(pid)}/search`, { q }),
  projectActivity: (pid: string, limit = 40) => get<Activity[]>(`${p(pid)}/activity`, { limit }),

  // properties
  createProperty: (pid: string, body: { entityType: EntityType; name: string; type: PropertyType; options?: { label: string; color: Color }[] }) =>
    post<{ id: string }>(`${p(pid)}/properties`, body),
  updateProperty: (pid: string, id: string, name: string) => patch(`${p(pid)}/properties/${id}`, { name }),
  deleteProperty: (pid: string, id: string) => del(`${p(pid)}/properties/${id}`),
  reorderProperties: (pid: string, ids: string[]) => put(`${p(pid)}/properties/order`, { ids }),
  createOption: (pid: string, propertyId: string, body: { label: string; color: Color }) =>
    post<PropertyOption>(`${p(pid)}/properties/${propertyId}/options`, body),
  updateOption: (pid: string, id: string, body: Partial<{ label: string; color: Color }>) => patch(`${p(pid)}/options/${id}`, body),
  deleteOption: (pid: string, id: string) => del(`${p(pid)}/options/${id}`),
  reorderOptions: (pid: string, propertyId: string, ids: string[]) => put(`${p(pid)}/properties/${propertyId}/options/order`, { ids }),
  setValue: (pid: string, entityId: string, propertyId: string, values: string[]) =>
    put(`${p(pid)}/values/${entityId}/${propertyId}`, { values }),

  // workflow lists
  createStage: (pid: string, body: { name: string; color: Color; kind: StageKind }) => post<Stage>(`${p(pid)}/stages`, body),
  updateStage: (pid: string, id: string, body: Partial<{ name: string; color: Color; kind: StageKind }>) => patch(`${p(pid)}/stages/${id}`, body),
  deleteStage: (pid: string, id: string, moveTo?: string) => del(`${p(pid)}/stages/${id}`, moveTo ? { moveTo } : undefined),
  reorderStages: (pid: string, ids: string[]) => put(`${p(pid)}/stages/order`, { ids }),
  createListItem: (pid: string, kind: ListKind, body: { label: string; color: Color }) => post<ListItem>(`${p(pid)}/${kind}`, body),
  updateListItem: (pid: string, kind: ListKind, id: string, body: Partial<{ label: string; color: Color }>) => patch(`${p(pid)}/${kind}/${id}`, body),
  deleteListItem: (pid: string, kind: ListKind, id: string) => del(`${p(pid)}/${kind}/${id}`),
  reorderListItems: (pid: string, kind: ListKind, ids: string[]) => put(`${p(pid)}/${kind}/order`, { ids }),

  // epics
  epics: (pid: string) => get<EpicListItem[]>(`${p(pid)}/epics`),
  epic: (pid: string, id: string) => get<Epic>(`${p(pid)}/epics/${id}`),
  createEpic: (pid: string, title: string) => post<Epic>(`${p(pid)}/epics`, { title }),
  updateEpic: (pid: string, id: string, body: Partial<{ title: string; body: Body }>) => patch(`${p(pid)}/epics/${id}`, body),
  deleteEpic: (pid: string, id: string) => del(`${p(pid)}/epics/${id}`),
  reorderEpics: (pid: string, ids: string[]) => put(`${p(pid)}/epics/order`, { ids }),

  // sprints
  sprints: (pid: string, epicId?: string) => get<SprintListItem[]>(`${p(pid)}/sprints`, epicId ? { epicId } : undefined),
  sprint: (pid: string, id: string) => get<Sprint>(`${p(pid)}/sprints/${id}`),
  createSprint: (
    pid: string,
    body: { epicId: string; title: string; goal: string; statusId: string | null; startDate: string | null; endDate: string | null },
  ) => post<{ id: string }>(`${p(pid)}/sprints`, body),
  updateSprint: (
    pid: string,
    id: string,
    body: Partial<{ title: string; goal: string; statusId: string | null; startDate: string | null; endDate: string | null; body: Body }>,
  ) => patch(`${p(pid)}/sprints/${id}`, body),
  deleteSprint: (pid: string, id: string) => del(`${p(pid)}/sprints/${id}`),

  // tasks
  tasks: (pid: string, filter: TaskScope) => get<TaskListItem[]>(`${p(pid)}/tasks`, filter as Record<string, unknown>),
  task: (pid: string, id: string) => get<Task>(`${p(pid)}/tasks/${id}`),
  createTask: (pid: string, body: { epicId: string; sprintId: string | null; stageId?: string; title: string }) =>
    post<Task>(`${p(pid)}/tasks`, body),
  updateTask: (
    pid: string,
    id: string,
    body: Partial<{ title: string; body: Body; epicId: string; sprintId: string | null; stageId: string; position: number }>,
  ) => patch<Task>(`${p(pid)}/tasks/${id}`, body),
  deleteTask: (pid: string, id: string) => del(`${p(pid)}/tasks/${id}`),
  addPRLink: (pid: string, taskId: string, url: string) => post<PRLink>(`${p(pid)}/tasks/${taskId}/pr-links`, { url }),
  deletePRLink: (pid: string, taskId: string, id: string) => del(`${p(pid)}/tasks/${taskId}/pr-links/${id}`),
  assign: (pid: string, taskId: string, userId: string) => put(`${p(pid)}/tasks/${taskId}/assignees/${userId}`),
  unassign: (pid: string, taskId: string, userId: string) => del(`${p(pid)}/tasks/${taskId}/assignees/${userId}`),
  setAssigneeStatus: (pid: string, taskId: string, userId: string, statusId: string | null) =>
    patch(`${p(pid)}/tasks/${taskId}/assignees/${userId}`, { statusId }),

  // collaboration
  comments: (pid: string, entityId: string) => get<Comment[]>(`${p(pid)}/entities/${entityId}/comments`),
  createComment: (pid: string, entityId: string, body: string) => post<Comment>(`${p(pid)}/entities/${entityId}/comments`, { body }),
  updateComment: (pid: string, id: string, body: string) => patch<Comment>(`${p(pid)}/comments/${id}`, { body }),
  deleteComment: (pid: string, id: string) => del(`${p(pid)}/comments/${id}`),
  activity: (pid: string, entityId: string) => get<Activity[]>(`${p(pid)}/entities/${entityId}/activity`),

  // files
  files: (pid: string, entityId?: string) => get<ProjectFile[]>(`${p(pid)}/files`, entityId ? { entityId } : undefined),
  uploadFile: (pid: string, file: File, description: string, links: string[], onProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append("description", description);
    form.append("links", JSON.stringify(links));
    form.append("file", file);
    return http
      .post<ProjectFile>(`${p(pid)}/files`, form, {
        onUploadProgress: (e) => e.total && onProgress?.(Math.round((e.loaded / e.total) * 100)),
      })
      .then((r) => r.data);
  },
  updateFile: (pid: string, id: string, body: Partial<{ name: string; description: string }>) => patch(`${p(pid)}/files/${id}`, body),
  deleteFile: (pid: string, id: string) => del(`${p(pid)}/files/${id}`),
  linkFile: (pid: string, id: string, entityId: string) => put(`${p(pid)}/files/${id}/links/${entityId}`),
  unlinkFile: (pid: string, id: string, entityId: string) => del(`${p(pid)}/files/${id}/links/${entityId}`),
  fileUrl: (pid: string, id: string, download = false) => `/api${p(pid)}/files/${id}/content${download ? "?download=1" : ""}`,
};

export type ListKind = "sprint-statuses" | "assignee-statuses";

export interface TaskScope {
  sprintId?: string;
  epicId?: string;
  backlog?: boolean;
  mine?: boolean;
}
